import type { AccountOption } from "@app/components/account-combobox";
import { BasisBadge } from "@app/components/basis-switcher";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import { BASIS_LABELS, basisParam, getBasis, useBasis } from "@app/lib/basis";
import { getPerspectiveId } from "@app/lib/perspective";
import PnlCellPanel, { type PnlCellScope } from "@app/pages/pnl/pnl-cell-panel";
import { environment } from "@app/relay/relay-environment";
import { Link, useNavigate } from "@tanstack/react-router";
import { Folder } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { pnlMonthPage_Query } from "@app/queries/pnlMonthPage_Query.graphql";
import type { pnlMonthPage_RehomeMutation } from "@app/queries/pnlMonthPage_RehomeMutation.graphql";
import type { pnlMonthPage_CreateCategoryMutation } from "@app/queries/pnlMonthPage_CreateCategoryMutation.graphql";
import { CellLink } from "@app/components/ui/cell-link";

export const DEFAULT_THRESHOLD = 500;

export const pnlMonthQuery = graphql`
  query pnlMonthPage_Query($month: String!, $perspectiveId: ID, $threshold: Int, $basis: BasisEnum) {
    perspective(perspectiveId: $perspectiveId) {
      id
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
      color
      parent {
        id
      }
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    allStakeholders {
      id
      name
    }
    pnlMonth(month: $month, perspectiveId: $perspectiveId, threshold: $threshold, basis: $basis) {
      month
      months
      threshold
      revenue
      rows {
        nodeId
        parentNodeId
        kind
        label
        depth
        strong
        amount
        ratio
        pnlLine
        color
        sliceType
        sliceAmountCents
        sliceWeight
        counterpartyCount
        category {
          id
          name
        }
        stakeholder {
          id
          name
        }
      }
    }
  }
`;

const createCategoryMutation = graphql`
  mutation pnlMonthPage_CreateCategoryMutation($input: CreateCategoryInputObject!) {
    createCategory(input: $input) {
      category {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const rehomeMutation = graphql`
  mutation pnlMonthPage_RehomeMutation($input: RehomeStakeholderInputObject!) {
    rehomeStakeholder(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

type Query = pnlMonthPage_Query["response"];
type Node = Query["pnlMonth"]["rows"][number];
type Category = Query["categories"][number];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

const AUTO_EXPAND_MS = 700;
const DRAG_TYPE = "application/x-cxcash-pnl-leaf";
const INDENT_PX = 24;
// Category.depth is capped at 3 server-side (CreateCategoryMutation), so a depth-3
// node can host no children.
const MAX_CATEGORY_DEPTH = 3;
// The statement lines whose top-level categories carry that pnl_line — the ones a new
// top-level category can be created under. Uncategorized is not a real line, and the
// subtotals/ratios have no categories of their own.
const CATEGORY_HOSTING_LINES = new Set(["COGS", "S_AND_M", "R_AND_D", "G_AND_A", "TAXES"]);

const monthLabel = (month: string) =>
  `${MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month} ${month.slice(0, 4)}`;

const endOfMonth = (month: string) => {
  const [year, index] = month.split("-").map(Number);
  return `${month}-${String(new Date(year, index, 0).getDate()).padStart(2, "0")}`;
};

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// A row's share of the month's revenue — the common-size P&L view. One decimal
// under 10% so the small counterparties don't all read 0%.
const percentOfRevenue = (value: number, revenue: number) => {
  if (revenue === 0) return "—";
  const pct = (value / revenue) * 100;
  const abs = Math.abs(pct);
  return `${abs >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
};

const ratioText = (ratio: number | null | undefined) =>
  ratio == null ? "—" : `${(ratio * 100).toFixed(0)}%`;

// The waterfall badge that says how a counterparty's cost lands on this category.
const sliceBadge = (node: Node): string | null => {
  if (node.kind !== "STAKEHOLDER") return null;
  if (node.sliceType === "ABSOLUTE") return money((node.sliceAmountCents ?? 0) / 100);
  if (node.sliceType === "REMAINDER") return "rest";
  const weight = node.sliceWeight ?? 1;
  return weight >= 1 ? null : `${Math.round(weight * 100)}%`;
};

// The synthetic tree nodes ("&"-joins, the root) are plumbing, not counterparties.
const isRealStakeholder = (stakeholder: { name: string }) =>
  !stakeholder.name.startsWith("&") &&
  !stakeholder.name.toLowerCase().includes("root stakeholder") &&
  stakeholder.name !== "_root";

// A category colour of white — the server's create default before we started
// inheriting the parent's — would draw an invisible icon on a white row. Fall back to
// slate so the folder is always legible.
const folderColor = (color: string | null | undefined) => {
  const value = (color ?? "").trim().toLowerCase();
  return value === "" || value === "#fff" || value === "#ffffff" ? "#94a3b8" : value;
};

// 1 for a top-level node — matches Category#depth, which the server caps at 3.
// Walked, not derived from the display path: a category name may itself contain "›".
const depthOf = (category: Category, byId: Map<string, Category>): number => {
  const parent = category.parent && byId.get(category.parent.id);
  return parent ? depthOf(parent, byId) + 1 : 1;
};

const isLeaf = (node: Node) =>
  node.kind === "STAKEHOLDER" || node.kind === "UNCATEGORIZED_STAKEHOLDER";

export default function PnlMonthPage({ queryRef }: { queryRef: PreloadedQuery<pnlMonthPage_Query> }) {
  const preloaded = usePreloadedQuery(pnlMonthQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const navigate = useNavigate();
  const notify = useToast();
  const basis = useBasis();

  const statement = data.pnlMonth;
  const [showPercent, setShowPercent] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cell, setCell] = useState<PnlCellScope | null>(null);
  const [thresholdText, setThresholdText] = useState(String(statement.threshold));

  // Re-sync the local mirror only when the route hands a NEW query (a month or
  // perspective switch), keyed on queryRef — NOT on `preloaded`: an imperative
  // refresh() writes the same query root into the store, and keying on
  // `preloaded` would re-emit the stale snapshot and clobber the fresh numbers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setThresholdText(String(statement.threshold)), [statement.month, statement.threshold]);

  const byParent = useMemo(() => {
    const map = new Map<string | null, Node[]>();
    for (const node of statement.rows) {
      const key = node.parentNodeId ?? null;
      const siblings = map.get(key) ?? [];
      siblings.push(node);
      map.set(key, siblings);
    }
    return map;
  }, [statement.rows]);

  const byId = useMemo(
    () => new Map(statement.rows.map((node) => [node.nodeId, node])),
    [statement.rows]
  );

  // Open down to the counterparty grain by default — seeing who is inside each
  // line IS this view. Only the below-threshold "Other" folds start closed, so
  // the long tail stays one click away instead of drowning the statement.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setExpanded(
      new Set(statement.rows.filter((node) => node.kind !== "OTHER").map((node) => node.nodeId))
    );
  }, [statement.month, statement.threshold]);

  const toggle = (nodeId: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });

  const rows = useMemo(() => {
    const out: { node: Node; hasChildren: boolean }[] = [];
    const walk = (node: Node) => {
      const children = byParent.get(node.nodeId) ?? [];
      out.push({ node, hasChildren: children.length > 0 });
      if (children.length > 0 && expanded.has(node.nodeId)) for (const child of children) walk(child);
    };
    for (const root of byParent.get(null) ?? []) walk(root);
    return out;
  }, [byParent, expanded]);

  const refresh = () =>
    fetchQuery<pnlMonthPage_Query>(environment, pnlMonthQuery, {
      month: statement.month,
      perspectiveId: getPerspectiveId() ?? undefined,
      threshold: statement.threshold,
      basis: basisParam(getBasis()) ?? undefined,
    })
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const months = statement.months;
  const index = months.indexOf(statement.month);
  const goToMonth = (month: string) => navigate({ to: "/pnl/$month", params: { month } });

  const applyThreshold = () => {
    const next = Math.max(0, Math.round(Number(thresholdText)));
    if (!Number.isFinite(next) || next === statement.threshold) {
      setThresholdText(String(statement.threshold));
      return;
    }
    navigate({ to: "/pnl/$month", params: { month: statement.month }, search: { threshold: next } });
  };

  // Full "Marketing › Paid acquisition" paths, so the picker is unambiguous when
  // subcategory names repeat under different parents; depth gates "+ Sub" against
  // the server's 3-level nesting cap.
  const { categoryPaths, categoryDepth } = useMemo(() => {
    const byCategoryId = new Map(data.categories.map((category) => [category.id, category]));
    const label = (category: Category): string => {
      const parent = category.parent && byCategoryId.get(category.parent.id);
      return parent ? `${label(parent)} › ${category.name}` : category.name;
    };
    const visible = data.categories.filter((category) => !category.name.startsWith("#"));
    return {
      categoryPaths: visible
        .map((category) => ({ id: category.id, path: label(category) }))
        .sort((a, b) => a.path.localeCompare(b.path)),
      categoryDepth: new Map(visible.map((category) => [category.id, depthOf(category, byCategoryId)])),
    };
  }, [data.categories]);

  // The one write path for a move, from the drag-drop and from the picker alike.
  // Only the edge that moves is named — the server re-homes it against the
  // counterparty's live allocation set, so the slices this month doesn't show
  // (a different category, a month with no spend) survive untouched.
  const move = (leaf: Node, toCategoryId: string, toLabel: string) => {
    const stakeholderId = leaf.stakeholder?.id;
    if (!stakeholderId) return;
    if (leaf.category?.id === toCategoryId) {
      notify({ message: `${leaf.label} is already in ${toLabel}.`, kind: "info" });
      return;
    }

    setSaving(leaf.nodeId);
    commitMutation<pnlMonthPage_RehomeMutation>(environment, {
      mutation: rehomeMutation,
      variables: {
        input: {
          stakeholderId,
          perspectiveId: data.perspective.id,
          toCategoryId,
          fromCategoryId: leaf.category?.id ?? null,
          sliceType: leaf.sliceType ?? null,
        },
      },
      // Two error channels: transport/schema errors arrive as the second arg,
      // domain validation errors inside the payload's own `errors` field.
      // Two error channels: transport/schema errors arrive as the second arg,
      // domain validation errors inside the payload's own `errors` field. Either way
      // REFETCH: the categorization is committed before the downstream re-derive runs,
      // so a failure there still changed the record — leaving the old numbers on screen
      // would be a lie about what the database now holds.
      onCompleted: (response, errors) => {
        const failure = errors?.[0]?.message ?? response.rehomeStakeholder?.errors?.[0]?.message;
        if (failure) {
          notify({ message: failure, kind: "error" });
          refresh().finally(() => setSaving(null));
          return;
        }
        notify({ message: `Moved ${leaf.label} → ${toLabel}`, kind: "success" });
        refresh().finally(() => setSaving(null));
      },
      onError: (error) => {
        notify({ message: error.message || "Couldn't move — please try again.", kind: "error" });
        refresh().finally(() => setSaving(null));
      },
    });
  };

  // Create a category on the fly: from a cost line (a new top-level node carrying that
  // line's pnl_line) or from a category row (a subcategory under it). A brand-new node
  // has no cost, so it lands as a $0 row — immediately usable as a drop target.
  const createCategory = (node: Node, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const underLine = node.kind === "LINE";

    setSaving(node.nodeId);
    commitMutation<pnlMonthPage_CreateCategoryMutation>(environment, {
      mutation: createCategoryMutation,
      variables: {
        input: {
          perspectiveId: data.perspective.id,
          name: trimmed,
          parentId: underLine ? null : node.category?.id ?? null,
          pnlLine: underLine ? node.pnlLine : null,
          // Inherit the parent's colour (the server otherwise defaults to white, which
          // renders as an invisible dot next to every sibling that has one).
          color: node.color ?? "#94a3b8",
        },
      },
      onCompleted: (response, errors) => {
        const failure = errors?.[0]?.message ?? response.createCategory?.errors?.[0]?.message;
        if (failure) {
          setSaving(null);
          notify({ message: failure, kind: "error" });
          return;
        }
        notify({ message: `Added ${trimmed} under ${node.label}`, kind: "success" });
        refresh().finally(() => setSaving(null));
      },
      onError: (error) => {
        setSaving(null);
        notify({ message: error.message || "Couldn't add the category — please try again.", kind: "error" });
      },
    });
  };

  // Open the transaction drill for a clicked number, scoped to this month and to
  // exactly what the row represents: a line, a category subtree, or one
  // counterparty inside it.
  const openCell = (node: Node) => {
    if (node.kind === "RATIO" || node.kind === "OTHER" || !node.pnlLine) return;
    setCell({
      line: node.pnlLine,
      categoryId: node.category?.id ?? null,
      counterpartyId: node.stakeholder?.id ?? null,
      from: `${statement.month}-01`,
      to: endOfMonth(statement.month),
      label: `${node.label} · ${monthLabel(statement.month)}`,
    });
  };

  const accountOptions: AccountOption[] = data.bankAccounts.map((account) => ({
    id: account.id,
    name: account.name,
    nickname: account.nickname,
    last4: account.last4,
  }));
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const hoverTimer = useRef<{ nodeId: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const clearHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current.timer);
    hoverTimer.current = null;
  };
  // Hover a collapsed category mid-drag to open it, so a drop target deeper in
  // the tree is reachable without letting go.
  const scheduleExpand = (nodeId: string) => {
    if (hoverTimer.current?.nodeId === nodeId || expanded.has(nodeId)) return;
    clearHover();
    hoverTimer.current = {
      nodeId,
      timer: setTimeout(() => setExpanded((previous) => new Set(previous).add(nodeId)), AUTO_EXPAND_MS),
    };
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/pnl" className="text-sm text-gray-500 hover:text-gray-800" title="Back to the full statement">
          ‹ P&L
        </Link>
        <h1 className="text-xl font-bold">
          {monthLabel(statement.month)} ({BASIS_LABELS[basis].toLowerCase()} basis)
        </h1>
        <BasisBadge />

        <div className="ml-auto flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5 text-gray-500" title="A counterparty at or above this reaches its own row; smaller ones fold into an expandable Other row">
            <span>Show counterparties over</span>
            <span className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min={0}
                step={100}
                value={thresholdText}
                onChange={(event) => setThresholdText(event.target.value)}
                onBlur={applyThreshold}
                onKeyDown={(event) => event.key === "Enter" && applyThreshold()}
                className="w-24 rounded border border-gray-200 pl-5 pr-2 py-1 tabular-nums"
              />
            </span>
          </label>

          <span
            className="inline-flex items-center rounded border border-gray-300 overflow-hidden text-xs font-semibold"
            title="Dollar amounts, or each number as a share of the month's revenue"
          >
            <button
              type="button"
              onClick={() => setShowPercent(false)}
              className={`px-2 py-1 ${!showPercent ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
            >
              $
            </button>
            <button
              type="button"
              onClick={() => setShowPercent(true)}
              className={`px-2 py-1 ${showPercent ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
            >
              %
            </button>
          </span>

          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={index <= 0 || loading}
              onClick={() => {
                setLoading(true);
                goToMonth(months[index - 1]);
              }}
              className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              title="Previous month"
            >
              ‹
            </button>
            <span className="px-1 tabular-nums font-medium text-gray-800">{statement.month}</span>
            <button
              type="button"
              disabled={index < 0 || index >= months.length - 1 || loading}
              onClick={() => {
                setLoading(true);
                goToMonth(months[index + 1]);
              }}
              className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              title="Next month"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500">
        <button
          type="button"
          onClick={() => setExpanded(new Set(statement.rows.map((node) => node.nodeId)))}
          className="shrink-0 whitespace-nowrap hover:text-gray-800 underline decoration-dotted underline-offset-2"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={() => setExpanded(new Set())}
          className="shrink-0 whitespace-nowrap hover:text-gray-800 underline decoration-dotted underline-offset-2"
        >
          Collapse all
        </button>
        <span className="text-gray-400">
          Drag a counterparty onto a category — or use its “Move to…” button — to correct the record. “+ Category”
          adds a bucket on the fly. Click any number for the transactions behind it.
        </span>
      </div>

      <div className="overflow-x-auto bg-white rounded border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 uppercase border-b border-gray-200">
              <th className="text-left font-medium px-3 py-2">Line</th>
              <th className="text-right font-medium px-3 py-2 w-40">
                {showPercent ? "% of revenue" : monthLabel(statement.month)}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ node, hasChildren }, index) => (
              <StatementRow
                key={node.nodeId}
                node={node}
                hasChildren={hasChildren}
                expanded={expanded.has(node.nodeId)}
                showPercent={showPercent}
                revenue={statement.revenue}
                striped={index % 2 === 1}
                saving={saving === node.nodeId}
                busy={saving !== null}
                categoryPaths={categoryPaths}
                canAddCategory={
                  node.kind === "CATEGORY"
                    ? (categoryDepth.get(node.category?.id ?? "") ?? MAX_CATEGORY_DEPTH) < MAX_CATEGORY_DEPTH
                    : node.kind === "LINE" && CATEGORY_HOSTING_LINES.has(node.pnlLine ?? "")
                }
                onAddCategory={(name) => createCategory(node, name)}
                onToggle={() => toggle(node.nodeId)}
                onCell={() => openCell(node)}
                onMove={(categoryId, label) => move(node, categoryId, label)}
                draggable={isLeaf(node) && saving === null}
                onDragStartLeaf={() => setDraggingId(node.nodeId)}
                onDragEndLeaf={() => {
                  setDraggingId(null);
                  clearHover();
                }}
                dropTarget={Boolean(draggingId) && node.kind === "CATEGORY"}
                onDragOverCategory={() => scheduleExpand(node.nodeId)}
                onDragLeaveCategory={clearHover}
                onDropLeaf={() => {
                  clearHover();
                  const dragged = draggingId && byId.get(draggingId);
                  setDraggingId(null);
                  if (dragged && node.category) move(dragged, node.category.id, node.label);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {cell && (
        <PnlCellPanel
          scope={cell}
          accountOptions={accountOptions}
          stakeholderOptions={stakeholderOptions}
          onClose={() => setCell(null)}
          onChanged={() => refresh()}
        />
      )}
    </div>
  );
}

function StatementRow({
  node,
  hasChildren,
  expanded,
  showPercent,
  revenue,
  striped,
  saving,
  busy,
  categoryPaths,
  canAddCategory,
  onAddCategory,
  onToggle,
  onCell,
  onMove,
  draggable,
  onDragStartLeaf,
  onDragEndLeaf,
  dropTarget,
  onDragOverCategory,
  onDragLeaveCategory,
  onDropLeaf,
}: {
  node: Node;
  hasChildren: boolean;
  expanded: boolean;
  showPercent: boolean;
  revenue: number;
  striped: boolean;
  saving: boolean;
  busy: boolean;
  categoryPaths: ReadonlyArray<{ id: string; path: string }>;
  canAddCategory: boolean;
  onAddCategory: (name: string) => void;
  onToggle: () => void;
  onCell: () => void;
  onMove: (categoryId: string, label: string) => void;
  draggable: boolean;
  onDragStartLeaf: () => void;
  onDragEndLeaf: () => void;
  dropTarget: boolean;
  onDragOverCategory: () => void;
  onDragLeaveCategory: () => void;
  onDropLeaf: () => void;
}) {
  const [dropHover, setDropHover] = useState(false);
  const [picking, setPicking] = useState(false);
  const [adding, setAdding] = useState(false);
  const isRatio = node.kind === "RATIO";
  const amount = node.amount ?? 0;
  const drillable = !isRatio && node.kind !== "OTHER" && Boolean(node.pnlLine) && amount !== 0;
  const badge = sliceBadge(node);

  const value = isRatio
    ? ratioText(node.ratio)
    : amount === 0
    ? "—"
    : showPercent
    ? percentOfRevenue(amount, revenue)
    : money(amount);

  return (
    <tr
      draggable={draggable}
      onDragStart={
        draggable
          ? (event) => {
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData(DRAG_TYPE, node.nodeId);
              onDragStartLeaf();
            }
          : undefined
      }
      onDragEnd={draggable ? onDragEndLeaf : undefined}
      onDragOver={
        dropTarget
          ? (event) => {
              if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
              event.preventDefault();
              setDropHover(true);
              onDragOverCategory();
            }
          : undefined
      }
      onDragLeave={
        dropTarget
          ? () => {
              setDropHover(false);
              onDragLeaveCategory();
            }
          : undefined
      }
      onDrop={
        dropTarget
          ? (event) => {
              event.preventDefault();
              setDropHover(false);
              onDropLeaf();
            }
          : undefined
      }
      className={`group border-b border-gray-100 last:border-b-0 hover:bg-sky-50 ${
        node.strong
          ? "bg-gray-100 font-semibold text-gray-900"
          : striped
          ? "bg-gray-50"
          : "bg-white"
      } ${dropHover ? "ring-2 ring-emerald-400" : ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${
        saving ? "opacity-60" : ""
      }`}
    >
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5" style={{ paddingLeft: `${node.depth * INDENT_PX}px` }}>
          {hasChildren ? (
            <button
              type="button"
              onClick={onToggle}
              title={expanded ? "Collapse" : "Expand"}
              className="shrink-0 w-4 text-gray-400 hover:text-gray-700 text-xs"
            >
              {expanded ? "▾" : "▸"}
            </button>
          ) : (
            <span className="shrink-0 w-4" />
          )}
          {/* The category marker sits in a slot reserved on EVERY row, not just the
              category ones: rendering it conditionally pushed a category's label right of
              its sibling counterparties, so equal-depth rows read as different levels.
              The folder replaces the former colour dot (which only ever rendered here) —
              it reads as "a bucket" at a glance and still carries the category's colour. */}
          <span className="w-3.5 shrink-0">
            {node.kind === "CATEGORY" && (
              <Folder size={14} aria-hidden style={{ color: folderColor(node.color) }} />
            )}
          </span>
          {badge && (
            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-500 tabular-nums">
              {badge}
            </span>
          )}
          <span className={node.kind === "CATEGORY" ? "text-gray-800" : isLeaf(node) ? "text-gray-600" : ""}>
            {node.label}
          </span>
          {node.kind === "OTHER" && (
            <span className="text-[10px] text-gray-400">(under the threshold)</span>
          )}

          {saving && <span className="text-xs text-gray-400 ml-1">Saving…</span>}

          {canAddCategory && !saving && (
            <span className="relative ml-1">
              <button
                type="button"
                disabled={busy}
                onClick={() => setAdding((open) => !open)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-30 whitespace-nowrap"
                title={
                  node.kind === "LINE"
                    ? `Add a top-level category under ${node.label}`
                    : `Add a subcategory under ${node.label}`
                }
              >
                + {node.kind === "LINE" ? "Category" : "Sub"}
              </button>
              {adding && (
                <NewCategoryPopover
                  parentLabel={node.label}
                  onClose={() => setAdding(false)}
                  onCreate={(name) => {
                    setAdding(false);
                    onAddCategory(name);
                  }}
                />
              )}
            </span>
          )}

          {isLeaf(node) && !saving && (
            <span className="relative ml-1">
              <button
                type="button"
                disabled={busy}
                onClick={() => setPicking((open) => !open)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-30"
                title="Move this counterparty to another category"
              >
                Move to…
              </button>
              {picking && (
                <CategoryPicker
                  options={categoryPaths}
                  onClose={() => setPicking(false)}
                  onPick={(categoryId, label) => {
                    setPicking(false);
                    onMove(categoryId, label);
                  }}
                />
              )}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
        {drillable ? (
          <CellLink onClick={onCell} title="View the transactions behind this number" className="decoration-dotted underline-offset-2 hover:text-inherit">
            {value}
          </CellLink>
        ) : (
          <span className={value === "—" ? "text-gray-300" : ""}>{value}</span>
        )}
      </td>
    </tr>
  );
}

// Name-and-create for a new category / subcategory, inline where it will live.
function NewCategoryPopover({
  parentLabel,
  onCreate,
  onClose,
}: {
  parentLabel: string;
  onCreate: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");

  return (
    <>
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 z-30 cursor-default" />
      <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded border border-gray-200 bg-white p-2 shadow-lg">
        <p className="px-1 pb-1 text-[11px] text-gray-400">New category under {parentLabel}</p>
        <div className="flex gap-1">
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") onClose();
              if (event.key === "Enter" && name.trim()) onCreate(name);
            }}
            placeholder="Name…"
            className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            disabled={!name.trim()}
            onClick={() => onCreate(name)}
            className="shrink-0 rounded bg-gray-900 px-2 py-1.5 text-xs font-medium text-white disabled:opacity-30"
          >
            Add
          </button>
        </div>
      </div>
    </>
  );
}

// Type-to-search over the perspective's categories, by full path. Page-scoped:
// it exists only to give the drag gesture a keyboard-and-scroll-free twin.
function CategoryPicker({
  options,
  onPick,
  onClose,
}: {
  options: ReadonlyArray<{ id: string; path: string }>;
  onPick: (categoryId: string, label: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? options.filter((option) => option.path.toLowerCase().includes(needle)).slice(0, 12)
    : options.slice(0, 12);

  return (
    <>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default"
      />
      <div className="absolute left-0 top-full z-40 mt-1 w-80 rounded border border-gray-200 bg-white p-2 shadow-lg">
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === "Escape" && onClose()}
          placeholder="Move to which category…"
          className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
        />
        <div className="mt-1 max-h-64 overflow-y-auto">
          {matches.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onPick(option.id, option.path)}
              className="block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50"
              title={option.path}
            >
              {option.path}
            </button>
          ))}
          {matches.length === 0 && <p className="px-2 py-1.5 text-sm text-gray-400">No category matches.</p>}
        </div>
      </div>
    </>
  );
}
